import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, ShieldCheck, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function ResetPassword() {
    const { token } = useParams();
    const nav = useNavigate();
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const strength = passwordStrength(pw);
    const strengthColors = { weak: "bg-red-500", fair: "bg-amber-500", strong: "bg-emerald-500" };

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 8) return toast.error("Password must be at least 8 characters");
        if (pw !== confirm) return toast.error("Passwords don't match");
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pw });
            setDone(true);
            setTimeout(() => nav("/login"), 2500);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't reset password — the link may have expired");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center bg-stone-50 px-4 py-8" data-testid="reset-password-page">
            <Toaster position="top-center" />
            <div className="w-full max-w-md bg-white border border-stone-200 p-8 space-y-5">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 grid place-items-center bg-sky-700 text-stone-50">
                        <KeyRound className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="label-eyebrow text-sky-700">Account recovery</div>
                        <h1 className="font-serif text-2xl text-stone-900">Choose a new password</h1>
                    </div>
                </div>

                {done ? (
                    <div className="space-y-3" data-testid="reset-done">
                        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 px-4 py-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-medium text-emerald-900">Password updated</div>
                                <p className="text-sm text-emerald-800 mt-1">
                                    Redirecting you to sign in…
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <p className="text-sm text-stone-600">
                            Pick a strong password. Reset links are single-use and expire in 1 hour.
                        </p>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">New password</Label>
                            <div className="relative mt-1">
                                <Input
                                    type={show ? "text" : "password"}
                                    value={pw}
                                    onChange={(e) => setPw(e.target.value)}
                                    autoComplete="new-password"
                                    minLength={8}
                                    required
                                    className="rounded-none border-stone-300 pr-10"
                                    data-testid="reset-new-password"
                                />
                                <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500" tabIndex={-1}>
                                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            {pw && (
                                <div className="mt-2">
                                    <div className="h-1 bg-stone-200 overflow-hidden">
                                        <div className={`h-full transition-all ${strengthColors[strength.level]}`} style={{ width: `${strength.percent}%` }} />
                                    </div>
                                    <div className="flex justify-between text-[10px] mt-1 uppercase tracking-widest">
                                        <span className={`${strength.level === "weak" ? "text-red-700" : strength.level === "fair" ? "text-amber-700" : "text-emerald-700"} font-semibold`}>{strength.label}</span>
                                        <span className="text-stone-400">{pw.length} chars</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Confirm new password</Label>
                            <Input
                                type={show ? "text" : "password"}
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                autoComplete="new-password"
                                required
                                className={`rounded-none border-stone-300 mt-1 ${confirm && confirm !== pw ? "border-red-400" : ""}`}
                                data-testid="reset-confirm-password"
                            />
                            {confirm && confirm !== pw && <div className="text-[11px] text-red-700 mt-1">Doesn't match</div>}
                        </div>
                        <Button type="submit" disabled={busy} className="w-full rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="reset-submit">
                            <ShieldCheck className="w-4 h-4 mr-2" />{busy ? "Updating…" : "Update password"}
                        </Button>
                    </form>
                )}

                <div className="text-[11px] text-stone-500 border-t border-stone-200 pt-3">
                    <Link to="/login" className="text-sky-700 hover:underline">Back to sign in</Link>
                </div>
            </div>
        </div>
    );
}

function passwordStrength(pw) {
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^a-zA-Z0-9]/.test(pw)) score++;
    if (score <= 2) return { level: "weak", label: "Weak", percent: 33 };
    if (score === 3 || score === 4) return { level: "fair", label: "Fair", percent: 66 };
    return { level: "strong", label: "Strong", percent: 100 };
}
